import { useCreateLearningRecommendation } from '@/shared/hooks/data-hooks/learning-recommendations/useCreateLearningRecommendation'
import { useLearningRecommendation } from '@/shared/hooks/data-hooks/learning-recommendations/useLearningRecommendation'
import { useLearningRecommendations } from '@/shared/hooks/data-hooks/learning-recommendations/useLearningRecommendations'
import {
  IRecommendation,
  RecommendationsListState,
} from '@/types/recommendations'
import { useCallback, useEffect, useRef, useState } from 'react'

const POLL_INTERVAL = 3000

type UseLearningRecommendationsDataFetchingOptions = {
  enabled: boolean
}

type UseLearningRecommendationsDataFetchingReturn = {
  lastRecommendation: IRecommendation | undefined
  listState: RecommendationsListState
  isLoading: boolean
  hasError: boolean
  generateRecommendationsAndStartPolling: () => Promise<void>
}

export function useLearningRecommendationsDataFetching({
  enabled,
}: UseLearningRecommendationsDataFetchingOptions): UseLearningRecommendationsDataFetchingReturn {
  const alreadyAskedForGeneration = useRef(false)
  const [shouldPollForRecommendations, setShouldPollForRecommendations] =
    useState(false)

  const {
    data: { data: recommendations } = {},
    isLoading: isLoadingRecommendations,
    isError: hasErrorLoadingRecommendations,
  } = useLearningRecommendations({
    sortBy: {
      updatedAt: 'desc',
      createdAt: 'desc',
    },
    queryOptions: {
      enabled,
      refetchInterval(query) {
        return enabled &&
          shouldPollForRecommendations &&
          (!Array.isArray(query.state.data?.data) ||
            !query.state.data?.data?.length)
          ? POLL_INTERVAL
          : false
      },
    },
  })

  const [recommendation] = Array.isArray(recommendations) ? recommendations : []

  const {
    data: { data: lastRecommendation } = {},
    isLoading: isLoadingLastRecommendation,
    isError: hasErrorLoadingLastRecommendation,
  } = useLearningRecommendation({
    id: recommendation?._id,
    queryOptions: {
      enabled: !!(enabled && recommendation?._id),
      refetchInterval(query) {
        return enabled &&
          !shouldPollForRecommendations &&
          query.state.data?.data?.status === 'generating'
          ? POLL_INTERVAL
          : false
      },
    },
  })

  const {
    mutateAsync: generateRecommendations,
    isError: hasErrorCreatingRecommendations,
  } = useCreateLearningRecommendation()

  const isLoading = isLoadingRecommendations || isLoadingLastRecommendation
  const hasError =
    hasErrorLoadingLastRecommendation ||
    hasErrorLoadingRecommendations ||
    hasErrorCreatingRecommendations

  const generateRecommendationsAndStartPolling = useCallback(async () => {
    await generateRecommendations()
    setShouldPollForRecommendations(true)
  }, [generateRecommendations])

  useEffect(() => {
    if (alreadyAskedForGeneration.current) {
      return
    }

    if (isLoading) {
      return
    }

    if (
      Array.isArray(recommendations) &&
      (recommendations.length === 0 ||
        recommendations.at(0)?.status === 'not_generated')
    ) {
      generateRecommendationsAndStartPolling()
      alreadyAskedForGeneration.current = true
    }
  }, [
    generateRecommendationsAndStartPolling,
    lastRecommendation?.status,
    recommendations,
    isLoading,
  ])

  useEffect(() => {
    if (
      shouldPollForRecommendations &&
      lastRecommendation?.status &&
      ['error', 'generating'].includes(lastRecommendation?.status)
    ) {
      setShouldPollForRecommendations(false)
    }
  }, [lastRecommendation, shouldPollForRecommendations])

  const listState = isLoading
    ? RecommendationsListState.Loading
    : hasError || lastRecommendation?.status === 'error'
      ? RecommendationsListState.Error
      : lastRecommendation?.status === 'generating' ||
          !Array.isArray(lastRecommendation?.items)
        ? RecommendationsListState.Scheduled
        : RecommendationsListState.Idle

  return {
    lastRecommendation,
    listState,
    isLoading,
    hasError,
    generateRecommendationsAndStartPolling,
  }
}
